import {
  ControlPolicyObject,
  ControlRule,
  useAsyncEffect,
} from '@jupiterone/jupiter-web-toolkit';
import React, { createContext, useContext, useMemo, useState } from 'react';
import { Policy, PolicyRule } from 'navbar/src/jupiterAccountSettings/types';
import { POLICIES_BY_RULE } from 'navbar/src/jupiterAccountSettings/constants';
import usePoliciesClient from './usePoliciesClient';

type PoliciesByGroup = {
  [groupId: string]: {
    [rule in PolicyRule]?: boolean;
  };
};

const AllPolicies = Object.values(Policy);
const useRelevantPoliciesLogic = () => {
  const policiesClient = usePoliciesClient();
  const [policies, setPolicies] = useState<ControlPolicyObject[]>([]);
  const policiesByGroup = useMemo(() => {
    // Reduce policies into a group map
    const groupPolicies = {} as Partial<PoliciesByGroup>;
    policies.forEach((policy) => {
      const policyName = policy.name as Policy;
      if (AllPolicies.includes(policyName)) {
        Object.entries(policy.policy.rules).forEach(([key, whom]) => {
          whom.forEach((who) => {
            const groupId = who['user.groups'].value;
            if (!(groupId in groupPolicies)) groupPolicies[groupId] = {};
            // TS I literally JUST created groupPolicies[groupId] (! override) ~Sivli
            groupPolicies[groupId]![key as PolicyRule] = true;
          });
        });
      }
    });
    return groupPolicies;
  }, [policies]);

  useAsyncEffect(async () => {
    const newPolicies = await policiesClient.getPolicies();
    setPolicies(newPolicies);
  }, []);

  const createPolicy = async (groupId: string, ruleName: PolicyRule) => {
    const policyName = POLICIES_BY_RULE[ruleName];
    const policyIndex = policies.findIndex(({ name }) => name === policyName);
    const shouldUpdate = policyIndex !== -1;
    const fallbackPolicy = shouldUpdate
      ? { ...policies[policyIndex] }
      : undefined;
    const rules = policies[policyIndex]?.policy?.rules || {};
    const rule = [...(rules[ruleName] || [])];
    rule.push({
      'user.groups': {
        comparison: 'includes',
        value: groupId,
      },
    } as ControlRule);
    const newControlPolicyObject: ControlPolicyObject = {
      name: policyName,
      policy: {
        rules: {
          ...rules,
          [ruleName]: rule,
        },
      },
    };
    // Optimistic update
    updatePolicyLocally(newControlPolicyObject, policyIndex);
    const fn = !shouldUpdate ? 'createPolicy' : 'updatePolicy';
    try {
      const controlPolicyObject = await policiesClient[fn](
        newControlPolicyObject
      );
      updatePolicyLocally(controlPolicyObject, policyIndex);
    } catch {
      updatePolicyLocally(
        fallbackPolicy,
        shouldUpdate ? policyIndex : policies.length
      );
    }
  };

  const removePolicy = async (groupId: string, ruleName: PolicyRule) => {
    if (!getGroupPolicyRule(groupId, ruleName)) return;
    const policyName = POLICIES_BY_RULE[ruleName];
    const policyIndex = policies.findIndex(({ name }) => name === policyName);
    if (policyIndex === -1) return;
    const fallbackPolicy = policies[policyIndex];
    if (!fallbackPolicy.policy.rules[ruleName]) return;
    // TS again if check just confirmed (! override) ~Sivli
    const rule = fallbackPolicy.policy.rules[ruleName]!.filter((group) => {
      return group['user.groups'].value !== groupId;
    });

    const newControlPolicyObject: ControlPolicyObject = {
      name: policyName,
      policy: {
        rules: {
          ...fallbackPolicy.policy.rules,
          [ruleName]: rule,
        },
      },
    };
    updatePolicyLocally(newControlPolicyObject, policyIndex);
    try {
      if (rule.length) {
        const controlPolicyObject = await policiesClient.updatePolicy(
          newControlPolicyObject
        );
        updatePolicyLocally(controlPolicyObject, policyIndex);
      } else {
        const response = await policiesClient.deletePolicy(ruleName);
        // The response is not something we can use
        // instead use fallback if this mutation failed
        if (!response.ok) throw 'API Fail';
      }
    } catch {
      updatePolicyLocally(fallbackPolicy, policyIndex);
    }
  };

  const updatePolicyLocally = (
    controlPolicyObject: ControlPolicyObject | undefined,
    index: number
  ) => {
    const newPolicies = [...policies];
    if (index !== -1) {
      if (controlPolicyObject) newPolicies[index] = controlPolicyObject;
      else newPolicies.splice(index, 1);
    } else {
      if (controlPolicyObject) newPolicies.push(controlPolicyObject);
    }
    setPolicies(newPolicies);
  };

  const getGroupPolicyRule = (groupId: string, rule: PolicyRule): boolean =>
    policiesByGroup[groupId]?.[rule] || false;

  const togglePolicyRule = async (
    groupId: string,
    rule: PolicyRule,
    enabled?: boolean
  ) => {
    const create = enabled || !getGroupPolicyRule(groupId, rule);
    if (create) {
      await createPolicy(groupId, rule);
    } else {
      await removePolicy(groupId, rule);
    }
  };

  return {
    getGroupPolicyRule,
    togglePolicyRule,
  };
};

type UseRelevantPolicies = ReturnType<typeof useRelevantPoliciesLogic>;
const RelevantPoliciesContext = createContext({} as UseRelevantPolicies);
const useRelevantPolicies = () => useContext(RelevantPoliciesContext);
export default useRelevantPolicies;

interface UseRelevantPoliciesContextProviderProps {
  children: React.ReactNode;
}

export const RelevantPoliciesProvider = ({
  children,
}: UseRelevantPoliciesContextProviderProps) => {
  const relevantPolicies = useRelevantPoliciesLogic();
  return (
    <RelevantPoliciesContext.Provider value={relevantPolicies}>
      {children}
    </RelevantPoliciesContext.Provider>
  );
};
